import { Navigate, type RouteObject } from "react-router";
import { AppShell } from "./AppShell";
import { RequireAuthority, RequireSession } from "./guards";
import { NAV } from "./navigation";
import { LoginPage } from "../features/auth/LoginPage";
import { ForgetPasswordPage } from "../features/auth/ForgetPasswordPage";
import { UnauthorizedPage } from "../features/auth/UnauthorizedPage";
import { Placeholder } from "../features/Placeholder";
import { UsersPage } from "../features/users/UsersPage";
import { RolesPage } from "../features/roles/RolesPage";
import { ProfilePage } from "../features/profile/ProfilePage";
import { ClergyPage } from "../features/clergy/ClergyPage";
import { CalendarPage } from "../features/calendar/CalendarPage";
import { NewsfeedPage } from "../features/newsfeed/NewsfeedPage";
import { ContentListPage } from "../features/content/ContentListPage";
import { RegistrationPage } from "../features/registration/RegistrationPage";
import { SurveyRoutes } from "../features/survey/SurveyRoutes";

/** Which phase of the migration plan owns each screen. */
const PHASE: Record<string, string> = {
  users: "phase 1",
  roles: "phase 1",
  profile: "phase 1",
  clergy: "phase 2",
  "calendar-management": "phase 2",
  newsfeed: "phase 2",
  "content-list": "phase 2",
  registration: "phase 3",
  survey: "phase 4",
};

/** Screens that are built. Everything else falls through to a Placeholder. */
const SCREENS: Record<string, React.ReactNode> = {
  users: <UsersPage />,
  roles: <RolesPage />,
  profile: <ProfilePage />,
  clergy: <ClergyPage />,
  "calendar-management": <CalendarPage />,
  newsfeed: <NewsfeedPage />,
  "content-list": <ContentListPage />,
  registration: <RegistrationPage />,
  survey: <SurveyRoutes />,
};

const dashboardChildren: RouteObject[] = [
  { index: true, element: <Navigate to="survey" replace /> },
  { path: "unauthorized", element: <UnauthorizedPage /> },
  ...NAV.map((entry) => ({
    path: `${entry.path}/*`,
    element: (
      <RequireAuthority path={entry.path}>
        {SCREENS[entry.path] ?? (
          <Placeholder title={entry.label} phase={PHASE[entry.path] ?? "a later phase"} />
        )}
      </RequireAuthority>
    ),
  })),
];

export const routes: RouteObject[] = [
  { path: "/", element: <Navigate to="/login" replace /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/forget-password", element: <ForgetPasswordPage /> },
  {
    path: "/dashboard",
    element: (
      <RequireSession>
        <AppShell />
      </RequireSession>
    ),
    children: dashboardChildren,
  },
  { path: "*", element: <Navigate to="/login" replace /> },
];
